import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../utils/api";
import { useAuth } from "../context/AuthContext";
import {
  CloudArrowUpIcon,
  DocumentArrowDownIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  HomeIcon,
  CloudIcon,
  CurrencyDollarIcon,
  ArrowRightIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";

function INRBulkUploadPage() {
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission } = useAuth();
  const [file, setFile] = useState(null);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [errors, setErrors] = useState([]);

  // Check permissions on component mount
  useEffect(() => {
    const checkPermissions = () => {
      const hasINRUpload = hasPermission("inr.bulk_upload");
      const hasInvestorsPermissions = hasAnyPermission([
        "investors.view",
        "investors.bulk_upload",
      ]);

      // If user only has regular investor permissions and not INR permissions,
      // redirect them to the regular investors page
      if (!hasINRUpload && hasInvestorsPermissions) {
        navigate("/bulk-upload", { replace: true });
        return;
      }

      // If user has neither permission, they shouldn't be here
      if (!hasINRUpload) {
        navigate("/dashboard", { replace: true });
        return;
      }
    };

    checkPermissions();
  }, [navigate, hasPermission, hasAnyPermission]);

  // If user doesn't have the right permissions, show redirect message
  if (!hasPermission("inr.bulk_upload")) {
    const hasInvestorsPermissions = hasAnyPermission([
      "investors.view",
      "investors.bulk_upload",
    ]);

    return (
      <div className="bg-gray-50 min-h-screen">
        <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="text-center">
                <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-yellow-400" />
                <h3 className="mt-2 text-lg font-medium text-gray-900">
                  Access Restricted
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  You don't have permission to access the INR bulk upload
                  functionality.
                </p>

                {hasInvestorsPermissions && (
                  <div className="mt-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center">
                        <UsersIcon className="h-5 w-5 text-blue-400 mr-2" />
                        <div className="text-left">
                          <h4 className="text-sm font-medium text-blue-800">
                            Regular Bulk Upload Available
                          </h4>
                          <p className="mt-1 text-sm text-blue-700">
                            You have access to regular investor bulk upload
                            functionality.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="mt-6">
                      <Link
                        to="/bulk-upload"
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        <UsersIcon className="h-5 w-5 mr-2" />
                        Go to Regular Bulk Upload
                        <ArrowRightIcon className="h-5 w-5 ml-2" />
                      </Link>
                    </div>
                  </div>
                )}

                <div className="mt-6">
                  <Link
                    to="/dashboard"
                    className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    <HomeIcon className="h-5 w-5 mr-2" />
                    Back to Dashboard
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFile(selectedFile);
    setMessage("");
    setUploadResult(null);
    setErrors([]);

    // Validate file type
    if (selectedFile) {
      const allowedTypes = [".csv", ".xls", ".xlsx", ".json"];
      const fileExtension = selectedFile.name
        .toLowerCase()
        .substring(selectedFile.name.lastIndexOf("."));

      if (!allowedTypes.includes(fileExtension)) {
        setMessage(
          "Invalid file type. Please select a CSV, XLS, XLSX, or JSON file."
        );
        setFile(null);
      }
    }
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage("Please select a file to upload.");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setUploadResult(null);
    setErrors([]);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await api.post("/api/inr-bulk-upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setUploadResult(response.data);

      if (response.data.success) {
        setMessage(
          `✅ Upload successful! ${response.data.summary.successful} INR investor records imported.`
        );
      } else {
        setMessage(`⚠️ Upload completed with issues. Check the details below.`);
      }

      if (response.data.errors) {
        setErrors(response.data.errors);
      }
    } catch (err) {
      console.error("Upload error:", err);
      const errorMessage =
        err.response?.data?.message || "Upload failed due to server error";
      setMessage(`❌ ${errorMessage}`);

      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
      }

      setUploadResult(err.response?.data || null);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadTemplate = async (format) => {
    try {
      setMessage(`Downloading ${format.toUpperCase()} template...`);

      const response = await api.get(
        `/api/inr-bulk-upload/template/${format}`,
        {
          responseType: "blob",
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `inr_investors_template.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setMessage(
        `✅ ${format.toUpperCase()} template downloaded successfully!`
      );
    } catch (error) {
      console.error("Template download error:", error);
      setMessage(`❌ Failed to download ${format.toUpperCase()} template`);
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Enhanced Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>•</span>
                <span>INR Bulk Upload Management</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="bg-yellow-50 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-yellow-700">
                  CSV, XLS, XLSX, JSON Supported
                </span>
              </div>
            </div>
          </div>

          {/* Page Title and Description */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <CurrencyDollarIcon className="h-8 w-8 text-gray-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                INR Bulk Upload
              </h1>
            </div>
            <p className="text-gray-600 max-w-3xl">
              Upload multiple INR investor records at once using CSV, Excel, or
              JSON files. Download templates, validate data, and import large
              datasets efficiently.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Form */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <CloudArrowUpIcon className="h-6 w-6 text-yellow-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">Upload File</h2>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select File (CSV, XLS, XLSX, JSON)
                </label>
                <input
                  type="file"
                  accept=".csv,.xls,.xlsx,.json"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-yellow-50 file:text-yellow-700 hover:file:bg-yellow-100"
                />
                {file && (
                  <p className="mt-2 text-sm text-gray-600">
                    Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              <button
                onClick={handleUpload}
                disabled={!file || isLoading}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="h-5 w-5 mr-2" />
                    Upload File
                  </>
                )}
              </button>
            </div>

            {/* File Format Info */}
            <div className="mt-6 p-4 bg-yellow-50 rounded-lg">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-yellow-400 mr-2 mt-0.5" />
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">
                    File Requirements
                  </h3>
                  <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                    <li>Supported formats: CSV, XLS, XLSX, JSON</li>
                    <li>Maximum file size: 5 MB</li>
                    <li>
                      <strong>Required fields:</strong> date, name, phone_number
                    </li>
                    <li>
                      <strong>Optional fields:</strong> received, sent, balance,
                      remarks
                    </li>
                    <li>Date format: YYYY-MM-DD format (e.g., 2025-10-13)</li>
                    <li>
                      Received and sent amounts should be positive numbers
                    </li>
                    <li>
                      Balance will be auto-calculated as (received - sent)
                    </li>
                    <li>
                      Phone number format: +91-XXXXXXXXXX or any valid format
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Download Templates */}
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <DocumentArrowDownIcon className="h-6 w-6 text-green-500 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">
                Download Templates
              </h2>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Download sample template files to see the correct format and
              required fields for INR investors.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => downloadTemplate("csv")}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
                Download CSV Template
              </button>

              <button
                onClick={() => downloadTemplate("xlsx")}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
                Download Excel Template
              </button>

              <button
                onClick={() => downloadTemplate("json")}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2 text-green-500" />
                Download JSON Template
              </button>
            </div>

            {/* Sample Data Preview */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="text-sm font-medium text-gray-800 mb-2">
                Sample Data Structure:
              </h3>
              <pre className="text-xs text-gray-600 overflow-x-auto">
                {`date: 2025-10-13
name: Rajesh Kumar
phone_number: +91-9876543210
received: 50000.00
sent: 20000.00
balance: 30000.00 (auto-calculated)
remarks: Initial investment`}
              </pre>
            </div>
          </div>
        </div>

        {/* Status Message */}
        {message && (
          <div
            className={`mt-6 p-4 rounded-md ${
              message.includes("✅")
                ? "bg-green-50 border border-green-200"
                : message.includes("⚠️")
                ? "bg-yellow-50 border border-yellow-200"
                : message.includes("❌")
                ? "bg-red-50 border border-red-200"
                : "bg-blue-50 border border-blue-200"
            }`}
          >
            <p
              className={`text-sm ${
                message.includes("✅")
                  ? "text-green-700"
                  : message.includes("⚠️")
                  ? "text-yellow-700"
                  : message.includes("❌")
                  ? "text-red-700"
                  : "text-blue-700"
              }`}
            >
              {message}
            </p>
          </div>
        )}

        {/* Upload Results */}
        {uploadResult && (
          <div className="mt-6 bg-white shadow rounded-lg p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Upload Results
            </h2>

            {/* Summary */}
            {uploadResult.summary && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-blue-700">
                    Total Records
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {uploadResult.summary.total}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-green-700">
                    Successful
                  </div>
                  <div className="text-2xl font-bold text-green-900">
                    {uploadResult.summary.successful}
                  </div>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <div className="text-sm font-medium text-red-700">Failed</div>
                  <div className="text-2xl font-bold text-red-900">
                    {uploadResult.summary.failed}
                  </div>
                </div>
              </div>
            )}

            {/* Errors */}
            {errors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-red-800">
                      Validation Errors
                    </h3>
                    <div className="mt-2 text-sm text-red-700">
                      <ul className="list-disc list-inside space-y-1 max-h-64 overflow-y-auto">
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Message */}
            {uploadResult.success && uploadResult.summary?.successful > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2 mt-0.5" />
                  <div>
                    <h3 className="text-sm font-medium text-green-800">
                      Upload Successful
                    </h3>
                    <p className="mt-1 text-sm text-green-700">
                      {uploadResult.summary.successful} INR investor records
                      have been successfully imported to the database.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default INRBulkUploadPage;
